/**
 * Queue helpers — run "read, then write what the read implies" one call at a time per key.
 *
 * A Dexie transaction alone does not serialise such work: the reads often run before the
 * transaction (dexie-sqlite does not roll back a failed one, so ops read first), and the Durable
 * Object's dexie-sqlite does not isolate overlapping transactions at all. Calls that share a key
 * queue up instead, in call order.
 *
 * In the Durable Object a readers-writer lock (server/src/pool/rwLock.ts, taken in UserDO.fetch)
 * now runs every request that may write alone, which also covers what no per-key queue can
 * (container delete/restore against a row of a task under it, bulkDelete / bulkRestore). These
 * queues stay for the callers the lock does not reach: the app in the browser, where overlapping
 * calls come from one UI (drag, quick-add save, sync-driven writes) and IndexedDB isolates
 * transactions but not the reads ops make before them, and the Node API server.
 *
 * INTERNAL — not exported from operations/index.ts, except serializeTaskMoves (the app's
 * quick-add save moves its draft under it).
 */

import Dexie from "dexie"
import { getDb } from "../runtime.js"

/** The tail of each key's queue: settles when the last call queued under that key has. */
const queues = new Map<string, Promise<unknown>>()

/**
 * Run `fn` once every earlier call sharing one of `keys` has settled, and make later calls with
 * any of them wait for this one. Keys are taken all at once, so calls never wait on each other
 * in a cycle.
 */
export function runQueued<T>(keys: readonly string[], fn: () => Promise<T>): Promise<T> {
  const unique = [...new Set(keys)]
  const before = unique.flatMap((key) => {
    const tail = queues.get(key)
    return tail ? [tail] : []
  })
  const run = (before.length > 0 ? Promise.all(before) : Promise.resolve()).then(fn)
  const tail = run.then(
    () => undefined,
    () => undefined,
  )
  for (const key of unique) queues.set(key, tail)
  void tail.then(() => {
    for (const key of unique) if (queues.get(key) === tail) queues.delete(key)
  })
  return run
}

/** Queue key of `kind` work on `id` in the current database. */
export function queueKey(kind: "checklist" | "task", id: string): string {
  return `${getDb().name}\u0000${kind}\u0000${id}`
}

/**
 * Run a change of where the tasks `ids` live — read each one's current parentPath, move it and
 * cascade its descendants (the checklist rows under `${parentPath}/${id}`) — after any other such
 * change to one of them. Two overlapping moves of one task would otherwise both cascade from the
 * path they read first, and the rows would end under the path the first one wrote.
 * addChecklistItem / addChecklistHeading read the task's path under it too, so a row is never
 * added under a path a move has just cascaded away from.
 *
 * Trashing and restoring a task (deleteTask, restoreFromTrash) and deleting one of its checklist
 * rows (deleteChecklistItem) run under it as well: the cascade writes deletedAt to the rows it
 * read, and a row deleted on its own in between would get the task's stamp and come back with it.
 *
 * Inside a caller's transaction `fn` runs at once: waiting for the queue there could let
 * IndexedDB auto-commit the caller's transaction.
 */
export function serializeTaskMoves<T>(ids: readonly string[], fn: () => Promise<T>): Promise<T> {
  if (Dexie.currentTransaction) return fn()
  return runQueued(
    ids.map((id) => queueKey("task", id)),
    fn,
  )
}
